import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  locationRequestsPath,
  locationCustomerPath,
  locationVehiclePath,
} from "@/routes";
import { Choice } from "./controls";
import { FormErrors, errorText } from "@/components/forms/form-errors";

export default function RequestForm({
  customer,
  vehicle,
}: {
  customer?: { id: string; revision: string; name: string } | null;
  vehicle?: {
    id: string;
    revision: string;
    make: string;
    model: string;
  } | null;
}) {
  const { auth, errors } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const path = locationRequestsPath(auth.location.id);
  const backPath = vehicle
    ? locationVehiclePath(auth.location.id, vehicle.id)
    : customer
      ? locationCustomerPath(auth.location.id, customer.id)
      : path;
  return (
    <AppLayout breadcrumbs={[{ title: t("requests.title"), href: path }]}>
      <Head title={t("requests.add")} />
      <PageCanvas>
        <h1 className="text-page font-display">{t("requests.add")}</h1>
        <p className="text-muted-foreground">{t("requests.intake_help")}</p>
        <Card>
          <CardContent className="pt-6">
            <Form action={path} method="post">
              {({ processing }) => (
                <div className="space-y-4">
                  <FormErrors
                    errors={errors || {}}
                    targets={{
                      channel_identity: "request-identity",
                      body: "request-body",
                      service_details: "request-services",
                    }}
                  />
                  {customer && (
                    <>
                      <p className="font-medium">
                        {customer.name}
                        {vehicle ? ` · ${vehicle.make} ${vehicle.model}` : ""}
                      </p>
                      <input
                        type="hidden"
                        name="request[customer_id]"
                        value={customer.id}
                      />
                      <input
                        type="hidden"
                        name="request[customer_revision]"
                        value={customer.revision}
                      />
                    </>
                  )}
                  {vehicle && (
                    <>
                      <input
                        type="hidden"
                        name="request[vehicle_id]"
                        value={vehicle.id}
                      />
                      <input
                        type="hidden"
                        name="request[vehicle_revision]"
                        value={vehicle.revision}
                      />
                    </>
                  )}
                  <Choice
                    id="request-channel"
                    name="request[channel]"
                    label={t("requests.channel")}
                    defaultValue="manual"
                    options={[
                      "manual",
                      "phone",
                      "walk_in",
                      "facebook",
                      "instagram",
                      "whatsapp",
                    ].map((id) => ({ id, name: t(`requests.channels.${id}`) }))}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="request-identity">
                      {t("requests.channel_identity")}
                    </Label>
                    <Input
                      id="request-identity"
                      aria-invalid={!!errors?.channel_identity}
                      aria-describedby={
                        errors?.channel_identity
                          ? "request-identity-error"
                          : undefined
                      }
                      name="request[channel_identity]"
                      maxLength={255}
                    />
                    {errors?.channel_identity && (
                      <p
                        id="request-identity-error"
                        className="text-sm text-destructive"
                      >
                        {errorText(errors.channel_identity)}
                      </p>
                    )}
                    <p className="text-muted-foreground text-sm">
                      {t("requests.identity_help")}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="request-body">{t("requests.body")}</Label>
                    <Textarea
                      id="request-body"
                      aria-invalid={!!errors?.body}
                      aria-describedby={
                        errors?.body ? "request-body-error" : undefined
                      }
                      name="request[body]"
                      required
                      maxLength={10000}
                      rows={5}
                    />
                    {errors?.body && (
                      <p
                        id="request-body-error"
                        className="text-sm text-destructive"
                      >
                        {errorText(errors.body)}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="request-services">
                      {t("requests.service_details")}
                    </Label>
                    <Textarea
                      id="request-services"
                      aria-invalid={!!errors?.service_details}
                      aria-describedby={
                        errors?.service_details
                          ? "request-services-error"
                          : undefined
                      }
                      name="request[service_details]"
                      maxLength={10000}
                    />
                    {errors?.service_details && (
                      <p
                        id="request-services-error"
                        className="text-sm text-destructive"
                      >
                        {errorText(errors.service_details)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={processing}>
                      {t("requests.save")}
                    </Button>
                    <Button variant="outline" render={<Link href={backPath} />}>
                      {t("customers.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>
          </CardContent>
        </Card>
      </PageCanvas>
    </AppLayout>
  );
}
